import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Compass, Plus, X, Trash2, Paperclip, Sparkles, Flame, Crown, Waves, Eraser, Gauge, Loader2, Table2, LayoutGrid, ArrowUp, ArrowDown, Presentation } from "lucide-react";
import PresentationStudioModal from "@/components/PresentationStudioModal";
import {
  ActifsProvider,
  OriginationHeaderStrip,
  OppActionButtons,
} from "@/pages/saas/OriginationExtensions";
import { FrictionHeatmap, DoctrineTestButton } from "@/pages/saas/V15Components";
import DocxUploader from "@/components/DocxUploader";
import ContextualImportButton from "@/components/ContextualImportButton";
import ProactiveWhisper from "@/components/ProactiveWhisper";
import ContextButton from "@/components/ContextButton";
import AnnuaireEntitySelector from "@/components/AnnuaireEntitySelector";
import DualValuation from "@/components/DualValuation";
import DocAttachTrigger from "@/components/DocAttachTrigger";
import HybridResearchPanel from "@/components/HybridResearchPanel";
import BatchFrictionGapButton from "@/components/BatchFrictionGapButton";
import ChainPositioning from "@/pages/saas/ChainPositioning";  // V23.15.29 — conservé (legacy, exportable pour Cartographie)
import OpportunitesDetectees from "@/pages/saas/OpportunitesDetectees";
import MarketSizeWidget from "@/components/MarketSizeWidget";
import BrasArmeAnalysisPanel from "@/components/BrasArmeAnalysisPanel";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import InlineGuide from "@/components/InlineGuide";
import SectorCombobox from "@/components/SectorCombobox";
import ValueChainPanel from "@/components/ValueChainPanel";  // V23.15.29 — vue rivière qui fusionne Fractale + Position dans la chaîne
import { useDemoMode } from "@/lib/demoMode";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const ORIG_IMPORT_FIELDS = [
  { key: "titre", label: "Titre", required: true },
  { key: "secteur", label: "Secteur", default: "Autre" },
  { key: "ticket_mEUR", label: "Ticket (M€)", type: "float", default: 0 },
  { key: "etape", label: "Étape", default: "qualification" },
  { key: "owner", label: "Owner", default: "" },
  { key: "prob_closing", label: "Probabilité closing %", type: "int", default: 25 },
  { key: "instrument", label: "Instrument", default: "" },
];

const STEP_COLOR = {
  identification: "#7F77DD",
  qualification: "#378ADD",
  structuration: "#378ADD",
  credibilisation: "#EF9F27",
  financement: "#1D9E75",
  participation: "#1D9E75",
  controle: "#A1A1AA",
};
const STEPS = ["identification", "qualification", "structuration", "credibilisation", "financement", "participation", "controle"];
const SECTEURS = ["Finance", "Industrie", "Santé", "Public", "Tech", "Infra", "Autre"];

function formatTicket(t) {
  if (t < 1) return `${(t * 1000).toFixed(0)} k€`;
  return `${t.toFixed(t < 10 ? 1 : 0)} M€`;
}

// Deal Scoring NOAM 2026-09
const scoreColor = (t) => (t >= 70 ? "#1D9E75" : t >= 45 ? "#EF9F27" : "#E24B4A");
const VERIF_LABEL = {
  verifie_en_ligne: { txt: "vérifié en ligne", c: "#1D9E75" },
  declare_non_verifie: { txt: "déclaré, non vérifié", c: "#EF9F27" },
  non_verifiable: { txt: "non vérifiable", c: "#A1A1AA" },
  recherche_indisponible: { txt: "recherche indisponible", c: "#E24B4A" },
};

function ScoreBadge({ opp, onOpen, onScore }) {
  const s = opp.noam_score;
  if (opp.noam_score_status === "scoring") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#378ADD]/10 text-[#378ADD] border border-[#378ADD]/30" data-testid={`orig-score-scoring-${opp.id}`}>
        <Loader2 className="w-2.5 h-2.5 animate-spin" /> Scoring…
      </span>
    );
  }
  if (!s) {
    return (
      <button onClick={onScore} title="Lancer le scoring de viabilité NOAM (vérification web incluse)"
        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-400 hover:text-white hover:border-[#7F77DD]/50"
        data-testid={`orig-score-btn-${opp.id}`}>
        <Gauge className="w-2.5 h-2.5" /> Scorer
      </button>
    );
  }
  const c = scoreColor(s.total);
  return (
    <button onClick={onOpen} title="Voir le détail du score et les leviers"
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border"
      style={{ color: c, borderColor: `${c}55`, background: `${c}15` }}
      data-testid={`orig-score-badge-${opp.id}`}>
      <Gauge className="w-2.5 h-2.5" /> {s.total}/100{s.sequencage?.priorite ? ` · P${s.sequencage.priorite}` : ""}
    </button>
  );
}

function ScoreDelta({ s, className = "" }) {
  if (s?.previous_total == null || s?.delta == null) return null;
  const up = s.delta >= 0;
  const Col = up ? "#1D9E75" : "#E24B4A";
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${className}`} style={{ color: Col }} data-testid="orig-score-delta">
      <Icon className="w-3 h-3" /> {up ? "+" : ""}{s.delta} <span className="text-zinc-500 font-normal">(préc. {s.previous_total})</span>
    </span>
  );
}

// Vue Portefeuille scoré 2026-06 — tri par score / priorité pour piloter le pipeline
function PortfolioTable({ data, onOpen, onScore }) {
  const all = Object.values(data?.by_step || {}).flat();
  const scored = all.filter((o) => o.noam_score).sort((a, b) => {
    const pa = a.noam_score?.sequencage?.priorite || 9;
    const pb = b.noam_score?.sequencage?.priorite || 9;
    if (pa !== pb) return pa - pb;
    return (b.noam_score?.total || 0) - (a.noam_score?.total || 0);
  });
  const unscored = all.filter((o) => !o.noam_score);
  const topLevier = (o) => {
    for (const cr of o.noam_score?.criteria || []) {
      if ((cr.leviers || []).length > 0) return cr.leviers[0];
    }
    return null;
  };
  return (
    <div className="noam-card overflow-x-auto" data-testid="orig-portfolio-table">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-500">
            <th className="px-3 py-2.5">#</th>
            <th className="px-3 py-2.5">Opportunité</th>
            <th className="px-3 py-2.5">Score</th>
            <th className="px-3 py-2.5">Δ</th>
            <th className="px-3 py-2.5">Priorité</th>
            <th className="px-3 py-2.5">Étape</th>
            <th className="px-3 py-2.5">Ticket</th>
            <th className="px-3 py-2.5">Levier n°1</th>
          </tr>
        </thead>
        <tbody>
          {scored.map((o, i) => {
            const s = o.noam_score;
            const c = scoreColor(s.total);
            const prio = s.sequencage?.priorite;
            const lev = topLevier(o);
            return (
              <tr key={o.id} onClick={() => onOpen(o)}
                className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                data-testid={`orig-portfolio-row-${o.id}`}>
                <td className="px-3 py-2.5 text-[11px] text-zinc-500">{i + 1}</td>
                <td className="px-3 py-2.5">
                  <div className="text-[12px] text-zinc-100 font-medium">{o.titre}</div>
                  <div className="text-[10px] text-zinc-500">{o.secteur}{o.owner ? ` · ${o.owner}` : ""}</div>
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[13px] font-bold" style={{ color: c }}>{s.total}</span>
                    <span className="text-[10px] text-zinc-600">/100</span>
                    <span className="w-14 h-1 rounded-full bg-white/5 overflow-hidden hidden sm:inline-block">
                      <span className="block h-full" style={{ width: `${s.total}%`, background: c }} />
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2.5"><ScoreDelta s={s} /></td>
                <td className="px-3 py-2.5">
                  {prio ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full border font-semibold"
                      style={{ color: prio === 1 ? "#1D9E75" : prio === 2 ? "#EF9F27" : "#A1A1AA", borderColor: "rgba(255,255,255,0.12)" }}>
                      P{prio}
                    </span>
                  ) : <span className="text-zinc-600 text-[10px]">—</span>}
                </td>
                <td className="px-3 py-2.5 text-[11px] text-zinc-400">{o.etape}</td>
                <td className="px-3 py-2.5 text-[11px] text-zinc-300 whitespace-nowrap">{formatTicket(o.ticket_mEUR)}</td>
                <td className="px-3 py-2.5 text-[11px] text-[#EF9F27] max-w-[280px]">
                  {lev ? <span className="line-clamp-2">↑ {lev}</span> : <span className="text-zinc-600">—</span>}
                </td>
              </tr>
            );
          })}
          {unscored.map((o) => (
            <tr key={o.id} className="border-b border-white/5 opacity-70" data-testid={`orig-portfolio-unscored-${o.id}`}>
              <td className="px-3 py-2.5 text-[11px] text-zinc-600">·</td>
              <td className="px-3 py-2.5">
                <div className="text-[12px] text-zinc-300">{o.titre}</div>
                <div className="text-[10px] text-zinc-600">{o.secteur}</div>
              </td>
              <td className="px-3 py-2.5" colSpan={2}>
                <ScoreBadge opp={o} onOpen={() => onOpen(o)} onScore={() => onScore(o)} />
              </td>
              <td className="px-3 py-2.5"><span className="text-zinc-600 text-[10px]">—</span></td>
              <td className="px-3 py-2.5 text-[11px] text-zinc-500">{o.etape}</td>
              <td className="px-3 py-2.5 text-[11px] text-zinc-500 whitespace-nowrap">{formatTicket(o.ticket_mEUR)}</td>
              <td className="px-3 py-2.5 text-[10px] text-zinc-600">non scoré</td>
            </tr>
          ))}
          {all.length === 0 && (
            <tr><td colSpan={8} className="px-3 py-6 text-center text-[11px] text-zinc-600 italic">Aucune opportunité en pipeline.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ScoreDetailModal({ opp, onClose, onRescore }) {
  const s = opp.noam_score;
  if (!s) return null;
  const c = scoreColor(s.total);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="orig-score-modal">
      <div className="noam-card w-full max-w-2xl p-5 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="noam-overline text-[#7F77DD] inline-flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Score de viabilité NOAM</div>
            <div className="text-base font-semibold text-zinc-100 mt-1">{opp.titre}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5 text-zinc-400" data-testid="orig-score-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-3 my-3 flex-wrap">
          <span className="text-3xl font-bold whitespace-nowrap shrink-0" style={{ color: c }}>{s.total}<span className="text-sm text-zinc-500">/100</span></span>
          <ScoreDelta s={s} className="text-[12px]" />
          {s.sequencage?.priorite && (
            <span className="text-[11px] px-2 py-1 rounded-full bg-[#7F77DD]/15 text-[#B4AFF0] border border-[#7F77DD]/30">
              Priorité {s.sequencage.priorite} — {s.sequencage.justification}
            </span>
          )}
        </div>
        {s.synthese && <div className="text-[12px] text-zinc-300 mb-3">{s.synthese}</div>}
        <div className="text-[10px] text-zinc-500 mb-3">
          Vérification web : {s.web_status} · scoré le {s.scored_at ? new Date(s.scored_at).toLocaleString("fr-CH") : ""} · {s.model}
          {s.trigger === "document" && <span className="text-[#378ADD]"> · re-scoré après ajout de document</span>}
          {s.docs_count > 0 && <span> · {s.docs_count} document(s) au dossier</span>}
        </div>
        <div className="space-y-3">
          {(s.criteria || []).map((cr) => {
            const v = VERIF_LABEL[cr.verification] || VERIF_LABEL.non_verifiable;
            return (
              <div key={cr.key} className="px-3 py-2.5 rounded-md border border-white/10 bg-white/[0.02]" data-testid={`orig-score-crit-${cr.key}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[12px] text-zinc-100 font-medium">{cr.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full border" style={{ color: v.c, borderColor: `${v.c}55` }}>{v.txt}</span>
                    <span className="text-[12px] font-semibold" style={{ color: scoreColor((cr.score / cr.max) * 100) }}>{cr.score}/{cr.max}</span>
                  </span>
                </div>
                <div className="w-full h-1 rounded-full bg-white/5 mt-1.5 overflow-hidden">
                  <div className="h-full" style={{ width: `${(cr.score / cr.max) * 100}%`, background: scoreColor((cr.score / cr.max) * 100) }} />
                </div>
                {cr.comment && <div className="text-[11px] text-zinc-400 mt-1.5">{cr.comment}</div>}
                {(cr.leviers || []).length > 0 && (
                  <ul className="mt-1.5 space-y-0.5">
                    {cr.leviers.map((l, i) => (
                      <li key={i} className="text-[11px] text-[#EF9F27] flex items-start gap-1.5">
                        <span className="mt-0.5">↑</span> {l}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={onRescore}
            className="text-[12px] px-3 py-1.5 rounded-md border border-white/10 text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1.5"
            data-testid="orig-rescore-btn">
            <Gauge className="w-3.5 h-3.5" /> Re-scorer (nouvelles informations)
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Origination() {
  return (
    <ActifsProvider>
      <OriginationInner />
    </ActifsProvider>
  );
}

function OriginationInner() {
  const { t } = useTranslation();
  const isDemo = useDemoMode();
  const [activeTab, setActiveTab] = useState("pipeline"); // V23.16.x — Pipeline deals = porte d'entrée (cohérence sidebar « Pipeline des deals »)
  const endpoint = isDemo ? "/origination/pipeline" : "/workspace/origination/pipeline";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ titre: "", secteur: "Finance", ticket_mEUR: 10, etape: "identification", owner: "", prob_closing: 30, instrument: "", annuaire_entity_ids: [] });
  const [busy, setBusy] = useState(false);
  const [attachOpp, setAttachOpp] = useState(null);
  const [brasArmeOpen, setBrasArmeOpen] = useState(false);
  const [scoreOpp, setScoreOpp] = useState(null);
  const [presentOpp, setPresentOpp] = useState(null); // Studio de présentation
  const [pipelineView, setPipelineView] = useState("kanban"); // kanban | portfolio (vue portefeuille scoré)

  const launchScore = async (opp) => {
    try {
      await api.post(`/workspace/origination/opportunities/${opp.id}/score`);
      notify.success("Scoring NOAM lancé (vérification web incluse, ~30-60s).");
      load();
      let tries = 0;
      const timer = setInterval(async () => {
        tries += 1;
        try {
          const r = await api.get(endpoint);
          setData(r.data);
          const all = Object.values(r.data?.by_step || {}).flat();
          const cur = all.find((x) => x.id === opp.id);
          if ((cur?.noam_score_status && cur.noam_score_status !== "scoring") || tries >= 24) clearInterval(timer);
        } catch { clearInterval(timer); }
      }, 5000);
    } catch (e) { notify.error(fmtError(e, "Scoring impossible.")); }
  };

  const load = () => api.get(endpoint).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Erreur"));
  useEffect(() => { setData(null); setError(null); load(); /* eslint-disable-next-line */ }, [isDemo]);
  // Audit cas zéro 26.08 — le kanban doit refléter un push effectué depuis un autre onglet
  useEffect(() => { if (activeTab === "pipeline") load(); /* eslint-disable-next-line */ }, [activeTab]);

  // V23.15.21 — Prefill par le Copilote IA (sessionStorage "noam_prefill_origination")
  // Champs whitelisted dans agent_module_registry.py :
  //   titre, secteur, ticket_mEUR, etape, owner, prob_closing, instrument, ltv_projete
  const [prefilledByAI, setPrefilledByAI] = useState(false);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("noam_prefill_origination");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      sessionStorage.removeItem("noam_prefill_origination");
      const payload = parsed && typeof parsed === "object" ? (parsed.data || parsed) : null;
      if (payload && typeof payload === "object") {
        setForm((prev) => ({
          ...prev,
          ...(typeof payload.titre === "string" ? { titre: payload.titre } : {}),
          ...(typeof payload.secteur === "string" ? { secteur: payload.secteur } : {}),
          ...(payload.ticket_mEUR != null && !isNaN(parseFloat(payload.ticket_mEUR))
            ? { ticket_mEUR: parseFloat(payload.ticket_mEUR) }
            : {}),
          ...(typeof payload.etape === "string" && STEPS.includes(payload.etape)
            ? { etape: payload.etape }
            : {}),
          ...(typeof payload.owner === "string" ? { owner: payload.owner } : {}),
          ...(payload.prob_closing != null && !isNaN(parseInt(payload.prob_closing, 10))
            ? { prob_closing: parseInt(payload.prob_closing, 10) }
            : {}),
          ...(typeof payload.instrument === "string" ? { instrument: payload.instrument } : {}),
        }));
        setShowAdd(true);
        setPrefilledByAI(true);
      }
    } catch {
      // ignore corrupted prefill
    }
  }, []);

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/workspace/origination/opportunities", { ...form, ticket_mEUR: parseFloat(form.ticket_mEUR), prob_closing: parseInt(form.prob_closing, 10) });
      const newOppId = r?.data?.id;
      setShowAdd(false);
      setPrefilledByAI(false);
      setForm({ titre: "", secteur: "Finance", ticket_mEUR: 10, etape: "identification", owner: "", prob_closing: 30, instrument: "", annuaire_entity_ids: [] });
      await load();
      // V16.2 — Auto-suggest frictions sur la nouvelle opp (fire-and-forget)
      if (newOppId) {
        notify.success("Opportunité créée — analyse Friction Gap en cours…");
        api.post("/workspace/origination/friction-gap", {
          scope: "opportunite",
          opportunite_id: newOppId,
          with_actifs: true,
        }).then((res) => {
          const fr = (res?.data?.frictions || []).length;
          if (fr > 0) notify.success(`Pré-analyse NOAM : ${fr} friction(s) identifiée(s) — cliquez 'Friction Gap' sur la carte pour les voir.`);
        }).catch(() => { /* silent — l'utilisateur peut toujours lancer manuellement */ });
      }
    } catch (err) { notify.error(fmtError(err, "Erreur")); }
    finally { setBusy(false); }
  };

  const moveStep = async (id, etape) => {
    try { await api.patch(`/workspace/origination/opportunities/${id}`, { etape }); await load(); }
    catch (err) { notify.error(fmtError(err, "Erreur")); }
  };

  const removeOpp = async (id) => {
    if (!window.confirm("Supprimer cette opportunité ?")) return;
    try { await api.delete(`/workspace/origination/opportunities/${id}`); await load(); }
    catch (err) { notify.error(fmtError(err, "Erreur")); }
  };

  if (error) return <div className="text-[#E24B4A] text-sm" data-testid="orig-error">Une erreur est survenue lors du chargement de cette vue. Réessayez dans quelques instants.</div>;
  if (!data) return <div className="text-zinc-400">{t("common.loading", { defaultValue: "Chargement…" })}</div>;

  // V23.15.39 — Doctrine 3Q banner (pipeline stagnant)
  const OrigBanner = () => <ThreeQPageHeader endpoint="/workspace/origination/top-priority" />;

  // Tab bar shared across all states — V23.16.x : recentrée sur les deals
  // (les fractales causale/ascendante vivent désormais dans le module Origination)
  const TabBar = () => (
    <div className="flex items-center gap-1 border-b border-white/5 overflow-x-auto flex-wrap" data-testid="orig-tabs">
      <button onClick={() => setActiveTab("pipeline")} data-testid="orig-tab-pipeline"
        className={`relative px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${activeTab === "pipeline" ? "text-white" : "text-zinc-400 hover:text-zinc-200"}`}>
        <Compass className="w-3.5 h-3.5" /> {t("saas.origination.tab_pipeline", { defaultValue: "Pipeline deals" })}
        {activeTab === "pipeline" && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#7F77DD]" />}
      </button>
      <button onClick={() => setActiveTab("opps_detectees")} data-testid="orig-tab-opps-detectees"
        className={`relative px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${activeTab === "opps_detectees" ? "text-white" : "text-zinc-400 hover:text-zinc-200"}`}>
        <Sparkles className="w-3.5 h-3.5" /> {t("saas.origination.tab_opps_detectees", { defaultValue: "Opportunités détectées" })}
        {activeTab === "opps_detectees" && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#EF9F27]" />}
      </button>
      <button onClick={() => setActiveTab("frictions")} data-testid="orig-tab-frictions"
        className={`relative px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${activeTab === "frictions" ? "text-white" : "text-zinc-400 hover:text-zinc-200"}`}>
        <Flame className="w-3.5 h-3.5" /> {t("saas.origination.tab_frictions", { defaultValue: "Carte des frictions" })}
        {activeTab === "frictions" && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#EF9F27]" />}
      </button>
      <button onClick={() => setActiveTab("chain")} data-testid="orig-tab-chain"
        className={`relative px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${activeTab === "chain" ? "text-white" : "text-zinc-400 hover:text-zinc-200"}`}>
        <Waves className="w-3.5 h-3.5" /> {t("saas.origination.tab_chain", { defaultValue: "Chaîne de valeur" })}
        {activeTab === "chain" && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#EF9F27]" />}
      </button>
    </div>
  );

  // Vague 4 — Onglet opportunités détectées (NOAM+Tavily)
  if (activeTab === "opps_detectees") {
    return (
      <div className="space-y-4" data-testid="origination-root">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#7F77DD] noam-overline"><Compass className="w-3 h-3" /> {t("saas.origination.overline", { defaultValue: "Tactique · Origination" })}</div>
            <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.origination.title", { defaultValue: "Opportunités d’origination" })}</h1>
          </div>
        </div>
        <OrigBanner />
        <TabBar />
        <OpportunitesDetectees />
      </div>
    );
  }

  // Vague 15 : Carte des frictions
  if (activeTab === "frictions") {
    return (
      <div className="space-y-4" data-testid="origination-root">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#7F77DD] noam-overline"><Compass className="w-3 h-3" /> {t("saas.origination.overline", { defaultValue: "Tactique · Origination" })}</div>
            <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.origination.title", { defaultValue: "Opportunités d’origination" })}</h1>
          </div>
        </div>
        <OrigBanner />
        <TabBar />
        <OriginationHeaderStrip />
        <FrictionHeatmap />
      </div>
    );
  }

  // V23.15.29 — Onglet "Chaîne de valeur" (vue rivière) — fusionne l'ancien
  // "Position dans la chaîne" (ChainPositioning) et "Fractale ↑" (OriginationFractaleAscendante).
  // Architecture : ValueChainPanel utilise un composant SVG ValueChainRiver props-driven,
  // ré-utilisable à terme dans la Cartographie des dépendances.
  if (activeTab === "chain") {
    return (
      <div className="space-y-4" data-testid="origination-root">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#7F77DD] noam-overline"><Compass className="w-3 h-3" /> {t("saas.origination.overline", { defaultValue: "Tactique · Origination" })}</div>
            <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.origination.title", { defaultValue: "Opportunités d’origination" })}</h1>
          </div>
        </div>
        <OrigBanner />
        <TabBar />
        <OriginationHeaderStrip />
        <ValueChainPanel />
      </div>
    );
  }

  if (!isDemo && data.is_empty) {
    return (
      <div className="space-y-6" data-testid="origination-root">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#7F77DD] noam-overline"><Compass className="w-3 h-3" /> {t("saas.origination.overline", { defaultValue: "Tactique · Origination" })}</div>
            <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.origination.title", { defaultValue: "Opportunités d’origination" })}</h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
              Suivez les pistes avant leur conversion en deal : qualification, frictions à lever et prochaines actions.
            </p>
          </div>
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="orig-add-btn"><Plus className="w-4 h-4" /> Ajouter une opportunité</button>
        </div>
        <OrigBanner />
        <TabBar />
        <OriginationHeaderStrip />
        <div className="noam-card p-12 text-center">
          <Compass className="w-12 h-12 text-[#7F77DD] mx-auto opacity-60" />
          <div className="noam-display text-xl mt-4">Aucune opportunité d’origination</div>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            Cette vue suit les opportunités d’origination. Ajoutez une piste pour commencer ; les deals déjà ouverts se consultent dans le Pipeline Deal.
          </p>
          <button onClick={() => setShowAdd(true)} className="btn-primary mt-6 mx-auto inline-flex" data-testid="orig-empty-add">
            <Plus className="w-4 h-4" /> Ajouter votre première opportunité
          </button>
          <a href="/app/deal-pipeline" className="mt-3 inline-flex text-sm text-[#7F77DD] hover:underline">Consulter le Pipeline Deal</a>
        </div>
        <AddOppModal open={showAdd} onClose={() => { setShowAdd(false); setPrefilledByAI(false); }} form={form} setForm={setForm} onSubmit={submitAdd} busy={busy} prefilledByAI={prefilledByAI} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="origination-root">
      {!isDemo && <ProactiveWhisper module="origination" />}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#7F77DD] noam-overline"><Compass className="w-3 h-3" /> {t("saas.origination.overline", { defaultValue: "Tactique · Origination" })}</div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.origination.title", { defaultValue: "Opportunités d’origination" })}</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            Suivez les pistes avant leur conversion en deal : qualification, frictions à lever et position dans la chaîne de valeur.
          </p>
        </div>
        {!isDemo && (
          <div className="flex items-center gap-2">
            <ModuleHelpButton moduleKey="origination" />
            <ContextButton moduleKey="origination" />
            <ContextualImportButton moduleKey="origination" targetFields={ORIG_IMPORT_FIELDS} onImported={() => window.location.reload()} />
            <BatchFrictionGapButton steps={STEPS} secteurs={SECTEURS} data={data} />
            <button
              onClick={async () => {
                if (!window.confirm("Purger les opportunités créées par les testing agents (titres commençant par TEST_, Test invalidator, Test_iter, …) ?")) return;
                try {
                  const r = await api.post("/workspace/origination/opportunities/cleanup-test");
                  if (r.data?.deleted_count > 0) {
                    notify.success(`${r.data.deleted_count} deal(s) de test purgé(s).`);
                    window.location.reload();
                  } else {
                    notify.info("Aucun deal de test détecté.");
                  }
                } catch (e) {
                  notify.error(fmtError(e, "Nettoyage impossible."));
                }
              }}
              className="text-xs px-2.5 py-1.5 rounded border border-[#E24B4A]/25 text-[#E24B4A]/90 hover:bg-[#E24B4A]/10 inline-flex items-center gap-1.5"
              data-testid="orig-cleanup-test-btn"
              title="Purge les opportunités créées par les agents de test"
            >
              <Eraser className="w-3.5 h-3.5" /> Nettoyer deals test
            </button>
            <button onClick={() => setBrasArmeOpen(true)} className="btn-secondary text-sm" data-testid="orig-bras-arme-btn" title="Identifier les grands acteurs compatibles">
              <Crown className="w-4 h-4 text-[#EFD127]" /> {t("bras_arme.button", { defaultValue: "Super Partenaires & Clients" })}
            </button>
            <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="orig-add-btn"><Plus className="w-4 h-4" /> Ajouter</button>
          </div>
        )}
      </div>

      <BrasArmeAnalysisPanel open={brasArmeOpen} onClose={() => setBrasArmeOpen(false)} sourceModule="origination" />

      <TabBar />
      <OriginationHeaderStrip />

      <InlineGuide
        id="origination-intro"
        title="Ici, vous pilotez votre pipeline de deals"
        bullets={[
          "Suivre chaque opportunité sur 7 étapes (identification → contrôle) pondérées par la probabilité de closing.",
          "Lancer une analyse « Friction Gap » sur une opportunité pour identifier ce qui bloque la conclusion.",
          "Activer « Super Partenaires & Clients » pour découvrir les grands acteurs compatibles avec votre offre.",
        ]}
        hint="Astuce : commencez par ajouter 2-3 opportunités réelles pour visualiser votre pipeline pondéré."
      />

      <MarketSizeWidget moduleKey="origination" title={t("saas.origination.title", { defaultValue: "Origination · pipeline deals" })} />

      <div className="grid md:grid-cols-4 gap-4">
        <div className="noam-card p-5" data-testid="orig-stat-count">
          <div className="text-xs text-zinc-400">Opportunités en pipeline</div>
          <div className="noam-display text-3xl font-bold mt-3">{data.total_count}</div>
        </div>
        <div className="noam-card p-5" data-testid="orig-stat-ticket">
          <div className="text-xs text-zinc-400">Ticket cumulé</div>
          <div className="noam-display text-3xl font-bold mt-3 text-[#378ADD]">{data.ticket_total_mEUR} M€</div>
        </div>
        <div className="noam-card p-5" data-testid="orig-stat-pondere">
          <div className="text-xs text-zinc-400">Pipeline pondéré (× probabilité)</div>
          <div className="noam-display text-3xl font-bold mt-3 text-[#1D9E75]">{data.ticket_pondere_mEUR} M€</div>
        </div>
        <div className="noam-card p-5" data-testid="orig-stat-leads-converted">
          <div className="text-xs text-zinc-400">Leads convertis ce mois</div>
          <div className="noam-display text-3xl font-bold mt-3 text-[#7F77DD]">{data.leads_converted_month || 0}</div>
        </div>
      </div>

      {/* V23.15.15 — Doctrine Brute/Net : valeur nette estimée compte tenu
          des frictions identifiées. Mode additif strict — ne remplace pas
          le ticket cumulé brut affiché ci-dessus. */}
      <DualValuation
        scope="origination_market"
        currencyLabel="M€"
        bruteLabel="Marché Origination — théorique brut"
        netteLabel="Capturable réaliste compte tenu des frictions identifiées"
        testIdPrefix="valuation-origination"
      />

      {/* Vue Portefeuille scoré : toggle kanban / table triée par score & priorité */}
      {!isDemo && (
        <div className="flex items-center gap-1" data-testid="orig-view-toggle">
          <button onClick={() => setPipelineView("kanban")}
            className={`text-[11px] px-2.5 py-1.5 rounded-md border inline-flex items-center gap-1.5 transition-colors ${pipelineView === "kanban" ? "border-[#7F77DD]/50 text-white bg-[#7F77DD]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}
            data-testid="orig-view-kanban-btn">
            <LayoutGrid className="w-3.5 h-3.5" /> Kanban
          </button>
          <button onClick={() => setPipelineView("portfolio")}
            className={`text-[11px] px-2.5 py-1.5 rounded-md border inline-flex items-center gap-1.5 transition-colors ${pipelineView === "portfolio" ? "border-[#7F77DD]/50 text-white bg-[#7F77DD]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}
            data-testid="orig-view-portfolio-btn">
            <Table2 className="w-3.5 h-3.5" /> Portefeuille scoré
          </button>
        </div>
      )}

      {!isDemo && pipelineView === "portfolio" ? (
        <PortfolioTable data={data} onOpen={(o) => setScoreOpp(o)} onScore={(o) => launchScore(o)} />
      ) : (
      <div className="overflow-x-auto -mx-2 px-2" data-testid="orig-kanban">
        <div className="flex gap-3 min-w-max pb-4">
          {data.steps.map((s) => {
            const items = data.by_step[s.key] || [];
            const c = STEP_COLOR[s.key] || "#A1A1AA";
            return (
              <div key={s.key} className="w-72 shrink-0" data-testid={`orig-col-${s.key}`}>
                <div className="flex items-center justify-between px-3 py-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
                    <span className="text-xs font-medium text-white">{s.label}</span>
                    <span className="text-[10px] text-zinc-500">· {items.length}</span>
                  </div>
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">#{s.ordre}</span>
                </div>
                <div className="space-y-2">
                  {items.map((o) => (
                    <div key={o.id} className="noam-card p-3 group" data-testid={`orig-card-${o.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm text-white font-medium leading-snug flex-1">{o.titre}</div>
                        <div className="flex items-center gap-1">
                          {!isDemo && (
                            <button onClick={() => setPresentOpp(o)} className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-[#7F77DD] opacity-60 group-hover:opacity-100" title="Studio de présentation" data-testid={`orig-present-${o.id}`}>
                              <Presentation className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button onClick={() => setAttachOpp(o)} className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-[#378ADD] opacity-60 group-hover:opacity-100" title="Pièces jointes" data-testid={`orig-attach-${o.id}`}>
                            <Paperclip className="w-3.5 h-3.5" />
                          </button>
                          {!isDemo && (
                            <button onClick={() => removeOpp(o.id)} className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-[#E24B4A] opacity-60 group-hover:opacity-100" data-testid={`orig-del-${o.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-300">{o.secteur}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-300">{formatTicket(o.ticket_mEUR)}</span>
                        {!isDemo && <ScoreBadge opp={o} onOpen={() => setScoreOpp(o)} onScore={() => launchScore(o)} />}
                      </div>
                      <div className="flex items-center justify-between mt-3 text-[10px]">
                        <span className="text-zinc-500">{o.owner}</span>
                        <div className="flex items-center gap-2">
                          <DocAttachTrigger module="pipeline" entityId={o.id} entityLabel={o.titre} />
                          <span className="text-zinc-400">{o.prob_closing}%</span>
                          <div className="w-12 h-1 rounded-full bg-white/5 overflow-hidden">
                            <div className="h-full" style={{ width: `${o.prob_closing}%`, background: c }} />
                          </div>
                        </div>
                      </div>
                      {!isDemo && (
                        <div className="mt-2 pt-2 border-t border-white/5">
                          <select
                            value={o.etape}
                            onChange={(e) => moveStep(o.id, e.target.value)}
                            className="w-full bg-black/40 border border-white/10 rounded text-[10px] text-zinc-300 px-1.5 py-1 focus:outline-none focus:border-[#378ADD]"
                            data-testid={`orig-step-${o.id}`}
                          >
                            {STEPS.map((sk) => (<option key={sk} value={sk}>{sk}</option>))}
                          </select>
                        </div>
                      )}
                      {o.instrument && <div className="mt-2 text-[10px] text-zinc-500">{o.instrument}{o.ltv_projete != null ? ` · LTV ${(o.ltv_projete * 100).toFixed(0)}%` : ""}</div>}
                      {!isDemo && (
                        <>
                          <OppActionButtons oppId={o.id} />
                          <div className="mt-1.5 flex items-center gap-1.5">
                            <DoctrineTestButton
                              sourceModule="origination"
                              sourceId={o.id}
                              label={o.titre}
                              getText={() => `Opportunité « ${o.titre} » — secteur ${o.secteur}, ticket ${o.ticket_mEUR} M€, étape ${o.etape}, probabilité ${o.prob_closing}%, instrument ${o.instrument || "?"}.`}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {items.length === 0 && (<div className="text-[11px] text-zinc-600 italic px-3 py-4 text-center border border-dashed border-white/5 rounded-lg">—</div>)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      <AddOppModal open={showAdd} onClose={() => { setShowAdd(false); setPrefilledByAI(false); }} form={form} setForm={setForm} onSubmit={submitAdd} busy={busy} prefilledByAI={prefilledByAI} />

      {scoreOpp && (
        <ScoreDetailModal
          opp={scoreOpp}
          onClose={() => setScoreOpp(null)}
          onRescore={() => { const o = scoreOpp; setScoreOpp(null); launchScore(o); }}
        />
      )}
      {presentOpp && (
        <PresentationStudioModal
          subject={{ type: "deal", id: presentOpp.id, label: presentOpp.titre }}
          onClose={() => setPresentOpp(null)}
        />
      )}

      {attachOpp && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={() => setAttachOpp(null)}>
          <div className="noam-card w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()} data-testid="orig-attach-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="noam-overline">Pièces jointes</div>
                <div className="noam-display text-base font-semibold mt-1">{attachOpp.titre}</div>
                <div className="text-xs text-zinc-500 mt-1">{attachOpp.secteur} · {formatTicket(attachOpp.ticket_mEUR)}</div>
              </div>
              <button onClick={() => setAttachOpp(null)} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
            <DocxUploader module="opportunite" targetId={attachOpp.id} title="" />
            <div className="mt-4 pt-4 border-t border-white/5">
              <HybridResearchPanel
                contextModule="generic"
                initialTopic={`${attachOpp.titre} ${attachOpp.secteur || ""}`.trim()}
                defaultMode="hybrid"
                title="Qualifier ce deal — signaux marché"
                testid="orig-hybrid-research"
                compact
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddOppModal({ open, onClose, form, setForm, onSubmit, busy, prefilledByAI = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="orig-add-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="noam-overline">Pipeline deals</div>
            <div className="noam-display text-lg font-semibold">Nouvelle opportunité</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        {prefilledByAI && (
          <div
            className="mb-4 px-3 py-2 rounded-md bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-[11px] text-[#EF9F27] inline-flex items-start gap-2"
            data-testid="orig-form-prefill-banner"
          >
            <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{"Pré-rempli par l'IA Copilote — vérifiez et ajustez avant de créer."}</span>
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Titre</label>
            <input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Ex : Acquisition fonds santé" className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="orig-form-titre" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Secteur</label>
              <SectorCombobox
                value={form.secteur}
                onChange={(v) => setForm({ ...form, secteur: v || "Autre" })}
                placeholder="Sélectionner ou saisir un secteur…"
                testId="orig-form-secteur"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Ticket (M€)</label>
              <input type="number" step="0.1" required value={form.ticket_mEUR} onChange={(e) => setForm({ ...form, ticket_mEUR: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="orig-form-ticket" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Étape</label>
              <select value={form.etape} onChange={(e) => setForm({ ...form, etape: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="orig-form-etape">
                {STEPS.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Probabilité (%)</label>
              <input type="number" min="0" max="100" required value={form.prob_closing} onChange={(e) => setForm({ ...form, prob_closing: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="orig-form-prob" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Owner</label>
              <input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Direction" className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="orig-form-owner" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Instrument</label>
              <input value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} placeholder="equity / dette / honoraires" className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="orig-form-instr" />
            </div>
          </div>
          <AnnuaireEntitySelector
            value={form.annuaire_entity_ids || []}
            onChange={(ids) => setForm({ ...form, annuaire_entity_ids: ids })}
            testid="orig-entity-selector"
            label="Contreparties · acquéreurs · intermédiaires"
            placeholder="Rechercher dans l'Annuaire…"
          />
          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="orig-form-submit">
            {busy ? "Enregistrement…" : "Ajouter au pipeline"}
          </button>
        </form>
      </div>
    </div>
  );
}
